import React, { useState, useEffect, useMemo } from 'react';
import { JourneyStep, StepAction, CompletionMethod } from '../../types/journey';
import { JourneyPlatformIntegrationService } from '../../services/journeyPlatformIntegrationService';
import { ProjectCreationSettingsService } from '../../services/projectCreationSettingsService';
import { DefinitionOfDone, DefinitionOfDoneCriteria } from './DefinitionOfDone';
import { DefinitionOfDoneModal } from './DefinitionOfDoneModal';
import { addVerificationSurvey } from '../../utils/journeyVerificationSurvey';
import { LongRunningLoadingState } from '../common/LongRunningLoadingState';
import { useJourneyPoints } from '../../hooks/useJourneyPoints';
import { usePoints } from '../../context/PointsContext';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import './StepDetailView.css';

interface StepDetailViewProps {
  step: JourneyStep;
  selectedIdea?: import('../../types/ai').BusinessIdea | null;
  validationSurvey?: { surveyId: string; ideaTitle: string; createdAt: string } | null;
  preReleaseProject?: { projectId: string; projectTitle: string; createdAt: string } | null;
  onCreateValidationSurveyForIdea?: (idea: import('../../types/ai').BusinessIdea, language?: string) => Promise<void> | void;
  onAction: (stepId: string, actionType: string, templateId?: string, selectedIdea?: import('../../types/ai').BusinessIdea, language?: string) => void;
  onComplete: (stepId: string, method: CompletionMethod, reflectionNotes?: string) => void;
  onSkip?: () => void; // Optional - skip functionality removed in favor of DoD completion
  isReviewMode?: boolean; // When true, disable actions (viewing completed step)
  journey?: import('../../types/journey').Journey; // Journey context for AI generation
}

export const StepDetailView: React.FC<StepDetailViewProps> = ({
  step,
  selectedIdea,
  validationSurvey,
  preReleaseProject,
  onCreateValidationSurveyForIdea,
  onAction,
  onComplete,
  onSkip,
  isReviewMode = false,
  journey
}) => {
  const { user } = useAuth();
  const [completedActions, setCompletedActions] = useState<Set<string>>(new Set());
  const [completedInstructions, setCompletedInstructions] = useState<Set<string>>(new Set());
  const [actionAffordability, setActionAffordability] = useState<Map<string, any>>(new Map());
  const [showEarningSuggestions, setShowEarningSuggestions] = useState<string | null>(null);
  const [isCreatingSurvey, setIsCreatingSurvey] = useState(false);
  const [isCreatingPreRelease, setIsCreatingPreRelease] = useState(false);
  const [isDoDModalOpen, setIsDoDModalOpen] = useState(false);
  const [pointsCheckError, setPointsCheckError] = useState<string | null>(null);
  
  const { t, uiLanguage } = useLanguage();
  // Initialize active phase tab based on step type
  const getInitialPhaseTab = () => {
    const titleEn = step.title || '';
    const titleZh = step.title_zh || '';
    if (step.stepIndex === 1 && (/^build mvp$/i.test(titleEn) || /^构建 mvp$/i.test(titleZh))) return 'setup';
    if (step.stepIndex === 2 && (/get early users/i.test(titleEn) || /获取早期用户/i.test(titleZh))) return 'setup';
    if (step.stepIndex === 3 && (/scale and grow/i.test(titleEn) || /扩展和增长/i.test(titleZh))) return 'week1';
    if (step.stepIndex === 1 && (/build product mvp/i.test(titleEn) || /构建产品 mvp/i.test(titleZh))) return 'plan';
    if (step.stepIndex === 2 && (/launch to existing clients/i.test(titleEn) || /向现有客户发布/i.test(titleZh))) return 'prepare';
    if (step.stepIndex === 2 && (/expand market reach/i.test(titleEn) || /扩大市场覆盖/i.test(titleZh))) return 'week1'; // "In Progress" Step 2
    return 'setup'; // fallback
  };

  const [activePhaseTab, setActivePhaseTab] = useState<string>(getInitialPhaseTab());

  // Reset active phase tab when step changes
  useEffect(() => {
    setActivePhaseTab(getInitialPhaseTab());
  }, [step.id, step.stepIndex, step.title]);

  // Definition of Done criteria for idea generation step (step index 0) with verification survey
  const [ideaGenerationDoDCriteria, setIdeaGenerationDoDCriteria] = useState<DefinitionOfDoneCriteria[]>([]);

  // Initialize idea generation DoD criteria with translations
  useEffect(() => {
    const baseCriteria: DefinitionOfDoneCriteria[] = [
      {
        id: 'generate_ideas',
        title: t('definitionOfDone.ideaGeneration.generateIdeas.title'),
        description: t('definitionOfDone.ideaGeneration.generateIdeas.description'),
        completed: false,
        required: true,
        validationMethod: 'manual',
        helpText: t('definitionOfDone.ideaGeneration.generateIdeas.helpText')
      },
      {
        id: 'select_top_idea',
        title: t('definitionOfDone.ideaGeneration.selectTopIdea.title'),
        description: t('definitionOfDone.ideaGeneration.selectTopIdea.description'),
        completed: false,
        required: true,
        validationMethod: 'manual',
        helpText: t('definitionOfDone.ideaGeneration.selectTopIdea.helpText')
      }
    ];

    const criteriaWithSurvey = addVerificationSurvey(baseCriteria, 'AI Idea Generation', 0, { t });
    setIdeaGenerationDoDCriteria(criteriaWithSurvey);
  }, [t]);

  // Definition of Done criteria for "Have Idea" stage Step 0 (Rapid Idea Validation)
  const [rapidIdeaValidationDoDCriteria, setRapidIdeaValidationDoDCriteria] = useState<DefinitionOfDoneCriteria[]>([]);

  // Definition of Done criteria for validation step (step index 1)
  // For the 'Build Your Solution' step we replace the default validation criteria with a single, actionable item
  const [validationDoDCriteria, setValidationDoDCriteria] = useState<DefinitionOfDoneCriteria[]>([]);

  // Definition of Done criteria for IN_PROGRESS Step 1 (Build Your Team)
  const [buildTeamDoDCriteria, setBuildTeamDoDCriteria] = useState<DefinitionOfDoneCriteria[]>([]);

  // Initialize Rapid Idea Validation DoD criteria for "Have Idea" stage Step 0
  useEffect(() => {
    if (step.stepIndex !== 0) {
      return;
    }

    const isRapidIdeaValidation = /rapid idea validation/i.test((step && step.title) || '');
    if (isRapidIdeaValidation) {
      const baseCriteria: DefinitionOfDoneCriteria[] = [
        {
          id: 'survey_responses',
          title: '≥20 validation survey responses collected',
          description: 'Collect at least 20 responses from your validation survey to get meaningful data.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: 'Share your survey widely across 5+ channels to reach your target audience.'
        },
        {
          id: 'assumptions_tested',
          title: 'Top 3 riskiest assumptions identified and tested',
          description: 'Identify and test your 3 riskiest assumptions through the survey.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: 'List 5-7 assumptions, rank by risk, and focus on testing the top 3.'
        },
        {
          id: 'demand_signal',
          title: 'Demand signal validated (≥15% strong interest)',
          description: 'At least 15% of respondents show strong interest in your solution.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: 'Analyze survey results to calculate the percentage showing strong interest.'
        },
        {
          id: 'price_sensitivity',
          title: 'Price sensitivity assessed with target range',
          description: 'Understand what customers are willing to pay for your solution.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: 'Review price-related survey responses to identify acceptable price range.'
        },
        {
          id: 'competitive_research',
          title: 'Competitive alternatives researched',
          description: 'Research 5-10 competitors and document their strengths and weaknesses.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: 'List competitors, their pricing, features, and customer complaints.'
        }
      ];
      setRapidIdeaValidationDoDCriteria(baseCriteria);
    }
  }, [step.stepIndex, step.title]);

  // Initialize Build Team DoD criteria for IN_PROGRESS Step 1
  useEffect(() => {
    if (step.stepIndex !== 1) {
      return;
    }

    // Check both English and Chinese titles
    const isBuildTeam = /build your team/i.test((step && step.title) || '') ||
      /组建您的团队/i.test((step && step.title) || '');
    if (isBuildTeam) {
      const baseCriteria: DefinitionOfDoneCriteria[] = [
        {
          id: 'key_roles_identified',
          title: uiLanguage === 'zh' ? '识别关键角色' : 'Key roles identified',
          description: uiLanguage === 'zh'
            ? '根据您的产品和增长阶段识别团队所需的关键角色。'
            : 'Identify the key roles needed for your team based on your product and growth stage.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: uiLanguage === 'zh'
            ? '列出您需要的具体角色（例如，开发人员、设计师、营销人员、销售人员）。'
            : 'List the specific roles you need (e.g., developer, designer, marketer, sales).'
        },
        {
          id: 'team_members_recruited',
          title: uiLanguage === 'zh' ? '招募 ≥2 名团队成员' : '≥2 team members recruited',
          description: uiLanguage === 'zh'
            ? '成功招募至少 2 名团队成员加入您的初创公司。'
            : 'Successfully recruit at least 2 team members to join your startup.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: uiLanguage === 'zh'
            ? '发布团队请求并通过平台与潜在团队成员建立联系。'
            : 'Post team requests and connect with potential team members through the platform.'
        }
      ];
      setBuildTeamDoDCriteria(baseCriteria);
      return;
    }
  }, [step.stepIndex, step.title, uiLanguage]);

  // Initialize validation DoD criteria based on step type
  useEffect(() => {
    // Only initialize for step index 1
    if (step.stepIndex !== 1) {
      return;
    }

    const isBuildStep = /build your solution/i.test((step && step.title) || '') ||
      /构建您的解决方案/i.test((step && step.title) || '');
    const isBuildMVP = /build mvp/i.test((step && step.title) || '') ||
      /构建 mvp/i.test((step && step.title) || '');

    if (isBuildStep) {
      const baseCriteria: DefinitionOfDoneCriteria[] = [
        {
          id: 'live_ultra_simple',
          title: uiLanguage === 'zh' ? '创建一个实时、超简单的工具版本' : 'Created a live, ultra-simple version of the tool',
          description: uiLanguage === 'zh'
            ? '部署一个可在公共 URL 访问的最小工作原型，展示核心功能。'
            : 'Deploy a minimal, working prototype reachable at a public URL that demonstrates the core action.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: uiLanguage === 'zh'
            ? '部署一个极其简单的端到端原型（无需账户，最小化UI）。上线后，标记为已完成以启用步骤完成。'
            : 'Deploy an extremely simple end-to-end prototype (no accounts, minimal UI). When this is live, mark this as completed to enable finishing the step.'
        }
      ];
      setValidationDoDCriteria(baseCriteria);
      return;
    }

    if (isBuildMVP) {
      const baseCriteria: DefinitionOfDoneCriteria[] = [
        {
          id: 'core_feature_deployed',
          title: uiLanguage === 'zh' ? '核心功能已部署并可在线访问' : 'Core feature deployed and accessible online',
          description: uiLanguage === 'zh'
            ? '您的 MVP 已部署，任何人都可以通过公共 URL 访问。'
            : 'Your MVP is deployed with a public URL that anyone can access.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: uiLanguage === 'zh'
            ? '部署到 Vercel、Netlify 或 Heroku，并验证 URL 可以正常工作。'
            : 'Deploy to Vercel, Netlify, or Heroku and verify the URL works.'
        },
        {
          id: 'landing_page_created',
          title: uiLanguage === 'zh' ? '创建包含演示/GIF 的着陆页' : 'Landing page with demo/GIF created',
          description: uiLanguage === 'zh'
            ? '创建一个展示 MVP 的着陆页，包含演示视频或 GIF。'
            : 'A landing page showcasing your MVP with a demo video or GIF.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: uiLanguage === 'zh'
            ? '创建一个简单的着陆页，包含标题、演示和行动号召。'
            : 'Create a simple landing page with headline, demo, and CTA.'
        },
        {
          id: 'email_signup_integrated',
          title: uiLanguage === 'zh' ? '集成电子邮件注册表单' : 'Email signup form integrated',
          description: uiLanguage === 'zh'
            ? '用户可以注册以获得通知或早期访问权限。'
            : 'Users can sign up to be notified or get early access.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: uiLanguage === 'zh'
            ? '添加 Mailchimp、ConvertKit 或简单的 Google 表单来收集注册。'
            : 'Add Mailchimp, ConvertKit, or simple Google Form for signups.'
        },
        {
          id: 'analytics_tracking',
          title: uiLanguage === 'zh' ? '实施基本分析跟踪' : 'Basic analytics tracking implemented',
          description: uiLanguage === 'zh'
            ? '跟踪页面浏览量、按钮点击和关键用户操作。'
            : 'Track page views, button clicks, and key user actions.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: uiLanguage === 'zh'
            ? '设置 Google Analytics 或 Plausible 并验证事件触发。'
            : 'Set up Google Analytics or Plausible and verify events fire.'
        },
        {
          id: 'end_to_end_tested',
          title: uiLanguage === 'zh' ? '端到端用户流程已测试并正常工作' : 'End-to-end user flow tested and working',
          description: uiLanguage === 'zh'
            ? '从着陆页到使用工具的完整用户旅程正常工作。'
            : 'Complete user journey works from landing to using the tool.',
          completed: false,
          required: true,
          validationMethod: 'manual',
          helpText: uiLanguage === 'zh'
            ? '在多个设备上测试，并让 3-5 位朋友测试。'
            : 'Test on multiple devices and get 3-5 friends to test it.'
        }
      ];
      setValidationDoDCriteria(baseCriteria);
      return;
    }

    // Fallback: existing validation criteria with verification survey
    const baseCriteria: DefinitionOfDoneCriteria[] = [
      {
        id: 'create_survey',
        title: uiLanguage === 'zh' ? '创建并发布验证调查' : 'Validation survey created and published',
        description: uiLanguage === 'zh'
          ? '创建一个"您会使用这个吗？"调查，包含至少 4 个验证问题来测试您想法的需求。'
          : 'Create a "Would You Use This?" survey with at least 4 validation questions to test demand for your idea.',
        completed: false,
        required: true,
        validationMethod: 'manual',
        helpText: uiLanguage === 'zh'
          ? '点击上方的"创建验证调查"按钮，根据您选择的想法生成调查。'
          : 'Click "Create Validation Survey" above to generate a survey based on your selected idea.'
      },
      {
        id: 'collect_responses',
        title: uiLanguage === 'zh' ? '收集最少回复数（10+）' : 'Minimum responses collected (10+)',
        description: uiLanguage === 'zh'
          ? '分享您的调查并收集至少 10 条潜在客户的回复，以获得有意义的验证数据。'
          : 'Share your survey and collect at least 10 responses from potential customers to get meaningful validation data.',
        completed: false,
        required: true,
        validationMethod: 'manual',
        helpText: uiLanguage === 'zh'
          ? '通过社交媒体、论坛、电子邮件列表或相关在线社区向目标受众分享您的调查链接。'
          : 'Share your survey link with your target audience through social media, forums, email lists, or relevant online communities.'
      },
      {
        id: 'validate_metrics',
        title: uiLanguage === 'zh' ? '顶级想法验证，问题频率达到 40%' : 'Top idea validated with 40% problem frequency',
        description: uiLanguage === 'zh'
          ? '至少 40% 的受访者每周或每天遇到该问题，表明市场需求强劲。'
          : 'Achieve at least 40% of respondents experiencing the problem weekly or daily, indicating strong market demand.',
        completed: false,
        required: true,
        validationMethod: 'manual',
        helpText: uiLanguage === 'zh'
          ? '查看您的调查回复，检查是否至少有 40% 的受访者经常遇到该问题。'
          : 'Review your survey responses to check if at least 40% of respondents experience the problem frequently.'
      }
    ];

    const criteriaWithSurvey = addVerificationSurvey(baseCriteria, 'Market Validation', 1, { t });
    setValidationDoDCriteria(criteriaWithSurvey);
  }, [step.stepIndex, step.title, uiLanguage, t]);

  // Definition of Done criteria for MVP / prototype step when a "Create MVP" action exists
  const [mvpDoDCriteria, setMvpDoDCriteria] = useState<DefinitionOfDoneCriteria[]>([]);
  const [mvpAllCompleted, setMvpAllCompleted] = useState<boolean>(false);

  // Initialize MVP DoD criteria with translations
  useEffect(() => {
    const baseCriteria: DefinitionOfDoneCriteria[] = [
      {
        id: 'mvp_prototype',
        title: t('definitionOfDone.mvp.completePrototype.title', { defaultValue: 'Complete prototype solution' }),
        description: t('definitionOfDone.mvp.completePrototype.description', { defaultValue: 'A working prototype that demonstrates the core feature and can be deployed for testing.' }),
        completed: false,
        required: true,
        validationMethod: 'manual',
        helpText: t('definitionOfDone.mvp.completePrototype.helpText', { defaultValue: 'Mark this when your prototype implements the core action, is testable, and is deployed.' })
      }
    ];

    setMvpDoDCriteria(baseCriteria);
    setMvpAllCompleted(baseCriteria.filter(c => c.required).every(c => c.completed));
  }, [t]);

  const handleMvpCriteriaChange = (updated: DefinitionOfDoneCriteria[]) => {
    setMvpDoDCriteria(updated);
    setMvpAllCompleted(updated.filter(c => c.required).every(c => c.completed));
  };

  const { userPoints } = usePoints();
  const {
    checkActionAffordability,
    calculateNetPoints,
    calculateStepPoints,
    getEarningSuggestions,
    loading: pointsLoading
  } = useJourneyPoints();

  // Load completed instructions from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(`instructions-${step.id}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setCompletedInstructions(new Set(parsed));
      } catch (error) {
        console.error('Error loading completed instructions:', error);
      }
    }
  }, [step.id]);

  // Handle instruction checkbox toggle
  const handleInstructionToggle = (instructionId: string) => {
    const newCompleted = new Set(completedInstructions);

    if (newCompleted.has(instructionId)) {
      // Unchecking - remove from completed
      newCompleted.delete(instructionId);
    } else {
      // Checking - add to completed
      newCompleted.add(instructionId);
      // Note: Points can be awarded by backend if needed in future
    }

    setCompletedInstructions(newCompleted);
    localStorage.setItem(`instructions-${step.id}`, JSON.stringify([...newCompleted]));
  };

  // Handle Complete All instructions
  const handleCompleteAllInstructions = () => {
    const instructionActions = step.actions.filter(action => action.type === 'instruction' && action.priority !== 'context');
    const allInstructionIds = instructionActions.map(action => {
      const actionHash = action.text.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
      return `${step.id}-${actionHash}`;
    });
    const newCompleted = new Set(allInstructionIds);

    setCompletedInstructions(newCompleted);
    localStorage.setItem(`instructions-${step.id}`, JSON.stringify([...newCompleted]));
  };

  // Handle Clear All instructions
  const handleClearAllInstructions = () => {
    setCompletedInstructions(new Set());
    localStorage.setItem(`instructions-${step.id}`, JSON.stringify([]));
  };

  // Check for completed actions - simple localStorage check, no periodic validation
  useEffect(() => {
    const completed = new Set<string>();
    step.actions.forEach(action => {
      if (JourneyPlatformIntegrationService.isActionCompleted(step.id, action.type)) {
        completed.add(action.type);
      }
    });
    setCompletedActions(completed);
  }, [step.id, step.actions]);

  // Check action affordability
  useEffect(() => {
    const checkAffordability = async () => {
      const affordabilityMap = new Map();

      for (const action of step.actions) {
        if (action.pointsCost > 0 && !completedActions.has(action.type)) {
          try {
            const result = await checkActionAffordability(action.pointsCost);
            affordabilityMap.set(action.type, result);
          } catch (error) {
            console.error('Error checking affordability for action:', action.type, error);
          }
        }
      }

      setActionAffordability(affordabilityMap);
    };

    if (step.actions.length > 0) {
      checkAffordability();
    }
  }, [step.actions, completedActions, userPoints, checkActionAffordability]);

  const handleActionClick = async (action: StepAction) => {
    // Handle Definition of Done action - open modal
    if (action.type === 'definitionOfDone') {
      setIsDoDModalOpen(true);
      return;
    }

    // Check for AI idea generation (Step 0) - requires 100 points
    if (action.type === 'ai' && step.stepIndex === 0) {
      const { AIPointsService } = await import('../../services/aiPointsService');
      const requiredPoints = AIPointsService.getPointsCost('ideaGeneration');
      const currentPoints = userPoints || 0;
      
      if (currentPoints < requiredPoints) {
        // Show error message using the same pattern as pre-release
        const errorMessage = t('journey.insufficientPointsForAI', {
          required: requiredPoints,
          current: currentPoints,
          needed: requiredPoints - currentPoints
        }) || `You need ${requiredPoints} points to generate ideas with AI. You currently have ${currentPoints} points. Earn ${requiredPoints - currentPoints} more points to unlock this feature.`;
        
        setPointsCheckError(errorMessage);
        return;
      }
      
      // Clear any previous errors when user has sufficient points
      setPointsCheckError(null);
    }

    // Journey actions do not cost points - affordability check removed
    
    // For AI-enhanced pre-release actions, generate content first with loading state
    if (action.type === 'preRelease' && selectedIdea) {
      console.log('🎯 Pre-release action triggered with idea:', selectedIdea.title);

      try {
        // ⚡ STEP 1: CHECK USER POINTS FIRST - Before any AI generation or navigation
        // This provides immediate feedback and prevents wasted resources
        console.log('💰 Checking user points before proceeding with pre-release...');
        
        // Use AIPointsService for consistent points checking
        const { AIPointsService } = await import('../../services/aiPointsService');
        const requiredPoints = await AIPointsService.getPreReleasePointsRequirement();

        console.log('📊 Points check:', {
          userPoints: user?.points || 0,
          requiredPoints,
          sufficient: (user?.points || 0) >= requiredPoints
        });

        // Check if user has sufficient points
        if (!user || user.points < requiredPoints) {
          const currentPoints = user?.points || 0;
          console.log('❌ Insufficient points for pre-release project creation');
          
          // Display error message with clear information
          const errorMessage = t('errors.insufficientPointsForAIEnhancedPreRelease', {
            required: requiredPoints,
            current: currentPoints
          });
          
          setPointsCheckError(errorMessage);
          
          // Error will stay visible until user manually dismisses it or earns enough points
          // No automatic clearing - user needs to read and understand the message
          
          return; // Stop here - don't proceed with AI generation or navigation
        }

        console.log('✅ User has sufficient points, proceeding with pre-release generation');
        
        // Clear any previous errors when user has sufficient points
        setPointsCheckError(null);

        // STEP 2: User has sufficient points - proceed with AI generation
        setIsCreatingPreRelease(true);

        // Import the AI service
        const { AIPreReleaseProjectService } = await import('../../services/aiPreReleaseProjectService');

        // Get journey context
        const urlParams = new URLSearchParams(window.location.search);
        const journeyId = urlParams.get('journeyId') || window.location.pathname.split('/').pop();
        const market = journey?.market || 'E-commerce';
        const persona = journey?.persona || 'skilled-maker';

        console.log('🤖 Generating AI pre-release project with system language:', { market, persona, language: uiLanguage });

        // Generate AI-enhanced pre-release project with system language
        const preReleaseData = await AIPreReleaseProjectService.generatePreReleaseProject(
          selectedIdea,
          market,
          persona,
          uiLanguage
        );

        console.log('✅ Pre-release project generated:', preReleaseData.title);

        // Store the generated data in sessionStorage for the creation page to use
        const preReleaseKey = `ai_pre_release_data_${journeyId}`;
        sessionStorage.setItem(preReleaseKey, JSON.stringify({
          ...preReleaseData,
          sourceIdea: selectedIdea,
          generatedAt: new Date().toISOString()
        }));

        console.log('💾 Stored pre-release data in sessionStorage');

        // Now navigate with the pre-generated data
        onAction(step.id, action.type, undefined, selectedIdea);

      } catch (error) {
        console.error('❌ Failed to generate pre-release project:', error);
        // Still navigate but without pre-generated data (will use fallback)
        onAction(step.id, action.type, undefined, selectedIdea);
      } finally {
        setIsCreatingPreRelease(false);
      }

      return;
    }

    // For other actions, proceed normally
    console.log('🎯 StepDetailView action handler:', {
      actionType: action.type,
      isPreRelease: action.type === 'preRelease',
      hasSelectedIdea: !!selectedIdea,
      selectedIdea: selectedIdea ? {
        title: selectedIdea.title,
        problem: selectedIdea.problem?.substring(0, 50)
      } : null
    });

    if (action.type === 'preRelease' && selectedIdea) {
      console.log('✅ Passing selected idea and system language to onAction');
      onAction(step.id, action.type, undefined, selectedIdea, uiLanguage);
    } else {
      console.log('❌ NOT passing selected idea:', {
        reason: action.type !== 'preRelease' ? 'not preRelease action' : 'no selected idea'
      });
      // Don't pass templateId - let the system use contextual matching instead
      // Pass system language for AI actions
      onAction(step.id, action.type, undefined, undefined, uiLanguage);
    }
  };

  const handleCreateSurveyClick = async () => {
    if (!selectedIdea || !onCreateValidationSurveyForIdea) return;

    try {
      setIsCreatingSurvey(true);
      await onCreateValidationSurveyForIdea(selectedIdea, uiLanguage);
    } catch (error) {
      console.error('Error creating validation survey:', error);
    } finally {
      setIsCreatingSurvey(false);
    }
  };

  const handleOpenSurveyClick = () => {
    if (!validationSurvey) return;
    const surveyUrl = `/questionnaires/${validationSurvey.surveyId}/edit?source=journey`;
    if (typeof window !== 'undefined') {
      const surveyWindow = window.open(surveyUrl, '_blank', 'noopener,noreferrer');
      if (!surveyWindow) {
        window.location.href = surveyUrl;
      }
    }
  };

  const handleCompleteStep = () => {
    // Complete step manually - DoD criteria have been checked in the UI
    onComplete(step.id, 'manual', 'All Definition of Done criteria completed');
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'survey':
        return '📊';
      case 'task':
        return '✅';
      case 'team':
        return '👥';
      case 'preRelease':
        return '🚀';
      case 'ai':
        return '🤖';
      case 'instruction':
        return '📋';
      default:
        return '⚡';
    }
  };

  const getNetPoints = (action: StepAction) => {
    return calculateNetPoints(action);
  };

  const formatNetPoints = (netPoints: number) => {
    return netPoints >= 0 ? `+${netPoints}` : `${netPoints}`;
  };

  const getStepPointsSummary = () => {
    return calculateStepPoints(step);
  };

  const formattedSurveyCreatedAt = useMemo(() => {
    if (!validationSurvey?.createdAt) {
      return null;
    }
    const createdDate = new Date(validationSurvey.createdAt);
    if (Number.isNaN(createdDate.getTime())) {
      return null;
    }
    return createdDate.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  }, [validationSurvey]);

  const hasMvpAction = step.actions.some(a => {
    const textToCheck = a.text || '';
    const textZhToCheck = a.text_zh || '';
    return /create development task|create mvp development task|create mvp/i.test(textToCheck) ||
           /创建开发任务|创建 mvp 开发任务|创建 mvp|构建.*mvp/i.test(textZhToCheck);
  });
  const isBuildStep = /build your solution/i.test((step && step.title) || '') ||
    /构建您的解决方案/i.test((step && step.title) || '');

  const createSurveyButtonLabel = validationSurvey ? t('navigation.createAnotherSurvey') : t('navigation.createSurvey');

  // Helper function to sort actions by priority
  const sortActionsByPriority = (actions: StepAction[]): StepAction[] => {
    const priorityOrder = { 'context': 0, 'critical': 1, 'important': 2, 'optional': 3 };

    return [...actions].sort((a, b) => {
      const aPriority = a.priority || 'important'; // Default to important if no priority
      const bPriority = b.priority || 'important';

      const aOrder = priorityOrder[aPriority] ?? 2;
      const bOrder = priorityOrder[bPriority] ?? 2;

      return aOrder - bOrder;
    });
  };

  // Group actions by phase for steps that have phases
  // Check both English and Chinese titles to support language switching
  const titleEn = step.title || '';
  const titleZh = step.title_zh || '';
  const isBuildMVPStep = step.stepIndex === 1 && (/^build mvp$/i.test(titleEn) || /^构建 mvp$/i.test(titleZh)); // Only exact "Build MVP"
  const isGetEarlyUsersStep = step.stepIndex === 2 && (/get early users/i.test(titleEn) || /获取早期用户/i.test(titleZh));
  const isScaleAndGrowStep = step.stepIndex === 3 && (/scale and grow/i.test(titleEn) || /扩展和增长/i.test(titleZh));
  const isInProgressBuildStep = step.stepIndex === 1 && (/build product mvp/i.test(titleEn) || /构建产品 mvp/i.test(titleZh));
  const isInProgressLaunchStep = step.stepIndex === 2 && (/launch to existing clients/i.test(titleEn) || /向现有客户发布/i.test(titleZh));
  const isInProgressExpandStep = step.stepIndex === 2 && (/expand market reach/i.test(titleEn) || /扩大市场覆盖/i.test(titleZh)); // Step 2 in "In Progress" stage
  const hasPhases = isBuildMVPStep || isGetEarlyUsersStep || isScaleAndGrowStep || isInProgressBuildStep || isInProgressLaunchStep || isInProgressExpandStep;

  const actionsByPhase = useMemo(() => {
    if (!hasPhases) return null;

    const phases: Record<string, StepAction[]> = {};
    step.actions.forEach(action => {
      if (action.phase) {
        if (!phases[action.phase]) {
          phases[action.phase] = [];
        }
        phases[action.phase].push(action);
      }
    });

    // Sort actions within each phase by priority
    Object.keys(phases).forEach(phase => {
      phases[phase] = sortActionsByPriority(phases[phase]);
    });

    return phases;
  }, [hasPhases, step.actions]);

  // Define phase orders and labels for different step types
  const getPhaseConfig = (): { order: string[]; labels: Record<string, string> } => {
    if (isBuildMVPStep) {
      return {
        order: ['setup', 'build', 'deploy'],
        labels: {
          setup: t('aiIdeaGenerator.phaseSetup'),
          build: t('aiIdeaGenerator.phaseBuild'),
          deploy: t('aiIdeaGenerator.phaseDeploy')
        }
      };
    }
    if (isGetEarlyUsersStep) {
      return {
        order: ['setup', 'launch', 'analyze'],
        labels: {
          setup: t('aiIdeaGenerator.phaseSetup'),
          launch: t('aiIdeaGenerator.phaseLaunch'),
          analyze: t('aiIdeaGenerator.phaseAnalyze')
        }
      };
    }
    if (isScaleAndGrowStep) {
      return {
        order: ['week1', 'week2', 'week4'],
        labels: {
          week1: t('aiIdeaGenerator.phaseWeek1'),
          week2: t('aiIdeaGenerator.phaseWeek2'),
          week4: t('aiIdeaGenerator.phaseWeek4')
        }
      };
    }
    if (isInProgressBuildStep) {
      return {
        order: ['plan', 'create', 'launch'],
        labels: {
          plan: t('aiIdeaGenerator.phasePlan'),
          create: t('aiIdeaGenerator.phaseCreate'),
          launch: t('aiIdeaGenerator.phaseLaunch')
        }
      };
    }
    if (isInProgressLaunchStep) {
      return {
        order: ['prepare', 'launch', 'optimize'],
        labels: {
          prepare: t('aiIdeaGenerator.phasePrepare'),
          launch: t('aiIdeaGenerator.phaseLaunch'),
          optimize: t('aiIdeaGenerator.phaseOptimize')
        }
      };
    }
    if (isInProgressExpandStep) {
      return {
        order: ['week1', 'week2', 'week3', 'week4'],
        labels: {
          week1: uiLanguage === 'zh' ? 'WEEK 1: 审计与优化' : 'WEEK 1: AUDIT & OPTIMIZE',
          week2: uiLanguage === 'zh' ? 'WEEK 2: 内容与 SEO' : 'WEEK 2: CONTENT & SEO',
          week3: uiLanguage === 'zh' ? 'WEEK 3: 付费与合作' : 'WEEK 3: PAID & PARTNERSHIPS',
          week4: uiLanguage === 'zh' ? 'WEEK 4: 发布与扩展' : 'WEEK 4: LAUNCH & SCALE'
        }
      };
    }
    return { order: [], labels: {} };
  };

  const phaseConfig = getPhaseConfig();
  const phaseOrder = phaseConfig.order;
  const phaseLabels = phaseConfig.labels;

  // Calculate phase completion percentage
  const getPhaseCompletion = (phase: string): { completed: number; total: number; percentage: number } => {
    if (!actionsByPhase || !actionsByPhase[phase]) {
      return { completed: 0, total: 0, percentage: 0 };
    }

    const phaseActions = actionsByPhase[phase].filter(
      action => action.type === 'instruction' && action.priority !== 'context'
    );
    const completedPhaseActions = phaseActions.filter(action => {
      const actionHash = action.text.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
      return completedInstructions.has(`${step.id}-${actionHash}`);
    });

    const total = phaseActions.length;
    const completed = completedPhaseActions.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, total, percentage };
  };

  // const getCompletionStatus = () => {
  //   if (!validationResults) return null;
  //   
  //   const completedCount = validationResults.completedCriteria.length;
  //   const totalCount = step.dodCriteria.length;
  //   
  //   return {
  //     completedCount,
  //     totalCount,
  //     isComplete: validationResults.isComplete,
  //     automaticCompletion: validationResults.automaticCompletion
  //   };
  // };

  return (
    <>
      <LongRunningLoadingState 
        operation="pre-release"
        isLoading={isCreatingPreRelease}
      />
      
      <LongRunningLoadingState 
        operation="validation-survey"
        isLoading={isCreatingSurvey}
      />
      
      <div className="step-detail-view">
        <div className="step-detail-header">
          <h2>{uiLanguage === 'zh' && step.title_zh ? step.title_zh : step.title}</h2>
        <div className="step-meta">
          <span className="step-index">{t('journey.step')} {step.stepIndex + 1}</span>
          <span className="step-time">
            {step.timeEstimateWeeks} {step.timeEstimateWeeks !== 1 ? t('journey.weeks') : t('journey.week')}
          </span>
        </div>
      </div>

      <div className="step-description">
        <p>{uiLanguage === 'zh' && step.description_zh ? step.description_zh : step.description}</p>
      </div>

      {/* Points check error message */}
      {pointsCheckError && (
        <div className="error-banner" style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, color: '#c33', fontWeight: 500 }}>{pointsCheckError}</p>
          </div>
          <button 
            onClick={() => setPointsCheckError(null)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0 8px',
              color: '#999'
            }}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {/* Phase Overview for steps with phases */}
      {hasPhases && actionsByPhase && phaseOrder.length > 0 && (
        <div className="phase-overview">
          <h4>{t('journey.phaseOverview') || 'Phase Overview'}</h4>
          <div className="phase-overview-grid">
            {phaseOrder.filter(phase => actionsByPhase[phase]).map(phase => {
              const completion = getPhaseCompletion(phase);
              const isCompleted = completion.percentage === 100;
              const isInProgress = completion.percentage > 0 && completion.percentage < 100;
              const isNotStarted = completion.percentage === 0;

              return (
                <div
                  key={phase}
                  className={`phase-overview-card ${isCompleted ? 'completed' : ''} ${isInProgress ? 'in-progress' : ''} ${isNotStarted ? 'not-started' : ''}`}
                  onClick={() => setActivePhaseTab(phase)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="phase-overview-header">
                    <span className="phase-overview-label">{phaseLabels[phase]}</span>
                    {isCompleted && <span className="phase-overview-status completed">✓ {t('journey.completed') || 'Completed'}</span>}
                    {isInProgress && <span className="phase-overview-status in-progress">⏳ {t('journey.inProgress') || 'In Progress'}</span>}
                    {isNotStarted && <span className="phase-overview-status not-started">○ {t('journey.notStarted') || 'Not Started'}</span>}
                  </div>
                  <div className="phase-overview-progress">
                    <div className="phase-overview-progress-bar">
                      <div
                        className="phase-overview-progress-fill"
                        style={{ width: `${completion.percentage}%` }}
                      />
                    </div>
                    <span className="phase-overview-progress-text">
                      {completion.completed}/{completion.total} {t('journey.actions') || 'actions'}
                    </span>
                  </div>
                  <div className="phase-overview-time">
                    {t('journey.estimatedTime') || 'Estimated time'}: {phase.includes('week') ? '1 week' : '3-5 days'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Display selected idea if available (only show on idea generation step) */}
      {selectedIdea && step.stepIndex === 0 && (
        <div className="selected-idea-display">
          <div className="selected-idea-header">
            <h4>🎯 {t('journey.yourSelectedIdea')}</h4>
          </div>
          <div className="selected-idea-content">
            <div className="idea-title">
              <strong>{selectedIdea.title}</strong>
            </div>
            <div className="idea-description">
              <span className="label">{t('journey.ideaSelectorProblem')}:</span> {selectedIdea.problem}
            </div>
            <div className="idea-solution">
              <span className="label">{t('journey.ideaSelectorSolution')}:</span> {selectedIdea.solution}
            </div>
            <div className="idea-customer">
              <span className="label">{t('journey.ideaSelectorTargetCustomer')}:</span> {selectedIdea.targetCustomer}
            </div>
            <div className="idea-feasibility">
              <span className="label">{t('journey.ideaSelectorTechnicalFeasibility')}:</span>
              <span className={`feasibility-badge ${selectedIdea.technicalFeasibility.toLowerCase()}`}>
                {selectedIdea.technicalFeasibility}
              </span>
            </div>
            {selectedIdea.skillsRequired && selectedIdea.skillsRequired.length > 0 && (
              <div className="idea-skills">
                <span className="label">{t('teams.requiredSkills')}:</span>
                <div className="skills-list">
                  {selectedIdea.skillsRequired.map((skill, index) => (
                    <span key={index} className="skill-chip">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {validationSurvey && (
              <div className="idea-survey">
                <div className="survey-details">
                  <span className="label">{t('journey.validationSurvey')}:</span>
                  <div className="survey-meta">
                    <span className="survey-id">#{validationSurvey.surveyId}</span>
                    <span className="survey-idea">for “{validationSurvey.ideaTitle}”</span>
                    {formattedSurveyCreatedAt && (
                      <span className="survey-created">{t('survey.created')} {formattedSurveyCreatedAt}</span>
                    )}
                  </div>
                </div>
                <div className="survey-actions">
                  <button
                    className="survey-link-button"
                    onClick={handleOpenSurveyClick}
                    type="button"
                    aria-label={t('survey.openSurvey')}
                  >
                    {t('survey.openSurvey')}
                  </button>
                </div>
              </div>
            )}
            {onCreateValidationSurveyForIdea && (
              <div className="idea-actions">
                <button
                  className="validation-survey-button"
                  onClick={handleCreateSurveyClick}
                  disabled={isCreatingSurvey}
                >
                  {isCreatingSurvey ? (
                    <span className="button-loading-content">
                      <span className="button-loading-spinner button-loading-spinner--inverse" aria-hidden="true"></span>
                      {t('survey.creatingSurvey')}
                    </span>
                  ) : (
                    createSurveyButtonLabel
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Display pre-release project if available (show on Test with Users step) */}
      {preReleaseProject && step.stepIndex === 2 && (
        <div className="selected-idea-display">
          <div className="selected-idea-header">
            <h4>🚀 {t('journey.yourPreReleaseProject')}</h4>
          </div>
          <div className="selected-idea-content">
            <div className="idea-survey">
              <div className="survey-details">
                <span className="label">Pre-Release Project:</span>
                <div className="survey-meta">
                  <span className="survey-idea">"{preReleaseProject.projectTitle}"</span>
                  {preReleaseProject.createdAt && (
                    <span className="survey-created">Created {new Date(preReleaseProject.createdAt).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}</span>
                  )}
                </div>
              </div>
              <div className="survey-actions">
                <button
                  className="survey-link-button"
                  onClick={() => {
                    const projectUrl = `/pre-release/${preReleaseProject.projectId}`;
                    window.open(projectUrl, '_blank', 'noopener,noreferrer');
                  }}
                  type="button"
                  aria-label="Open pre-release project in a new tab"
                >
                  View Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="step-actions">
        <div className="actions-header">
          <h3>{t('journey.actions')}</h3>
          {step.actions.some(action => action.type === 'instruction') && !isReviewMode && (
            <div className="instruction-controls">
              <button
                className="instruction-control-button complete-all"
                onClick={handleCompleteAllInstructions}
                title={t('journey.completeAll')}
              >
                ✓ {t('journey.completeAll')}
              </button>
              <button
                className="instruction-control-button clear-all"
                onClick={handleClearAllInstructions}
                title={t('journey.clearAll')}
              >
                ✕ {t('journey.clearAll')}
              </button>
              <span className="instruction-progress">
                {(() => {
                  if (hasPhases && actionsByPhase) {
                    // For phase-based steps, count completed instructions across all phases
                    const allPhaseInstructions = Object.values(actionsByPhase).flat()
                      .filter(action => action.type === 'instruction' && action.priority !== 'context');
                    const completedPhaseInstructions = allPhaseInstructions.filter(action => {
                      const actionHash = action.text.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
                      return completedInstructions.has(`${step.id}-${actionHash}`);
                    });
                    return t('aiIdeaGenerator.instructionsCompleted', {
                      completed: completedPhaseInstructions.length,
                      total: allPhaseInstructions.length
                    });
                  } else {
                    // For non-phase steps, use the original logic
                    return t('aiIdeaGenerator.instructionsCompleted', {
                      completed: completedInstructions.size,
                      total: step.actions.filter(a => a.type === 'instruction' && a.priority !== 'context').length
                    });
                  }
                })()}
              </span>
            </div>
          )}
        </div>

        {/* Phase tabs for steps with phases */}
        {hasPhases && actionsByPhase && (
          <div className="phase-tabs">
            {phaseOrder.filter(phase => actionsByPhase[phase]).map(phase => {
              const completion = getPhaseCompletion(phase);
              const isCompleted = completion.percentage === 100;
              const isInProgress = completion.percentage > 0 && completion.percentage < 100;

              return (
                <button
                  key={phase}
                  className={`phase-tab ${activePhaseTab === phase ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isInProgress ? 'in-progress' : ''}`}
                  onClick={() => setActivePhaseTab(phase)}
                  title={`${completion.completed} of ${completion.total} actions completed (${completion.percentage}%)`}
                >
                  <span className="phase-label">{phaseLabels[phase]}</span>
                  {completion.total > 0 && (
                    <span className="phase-progress">
                      {completion.completed}/{completion.total}
                    </span>
                  )}
                  {isCompleted && <span className="phase-check">✓</span>}
                </button>
              );
            })}
          </div>
        )}

        <div className="actions-grid">
          {(hasPhases && actionsByPhase ?
            actionsByPhase[activePhaseTab] :
            sortActionsByPriority(step.actions)
          )?.map((action, index) => {
            const isCompleted = completedActions.has(action.type);
            const isGenerateBusinessIdeasAction = action.type === 'ai' && step.stepIndex === 0;
            const shouldDisableGenerateAction = !!selectedIdea && isGenerateBusinessIdeasAction;
            const isInstruction = action.type === 'instruction';
            const isDefinitionOfDone = action.type === 'definitionOfDone';
            const isMvpCreateAction = /create development task|create mvp development task|create mvp/i.test(action.text);

            if (isInstruction) {
              // Create unique ID based on action text hash to avoid conflicts between phases
              const actionHash = action.text.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
              const instructionId = `${step.id}-${actionHash}`;
              const isInstructionCompleted = completedInstructions.has(instructionId);
              const isContextAction = action.priority === 'context';

              return (
                <div
                  key={index}
                  className={`action-card instruction-card ${isInstructionCompleted ? 'completed' : ''} ${isContextAction ? 'context-header' : ''}`}
                  onClick={() => !isReviewMode && !isContextAction && handleInstructionToggle(instructionId)}
                  style={{ cursor: isReviewMode || isContextAction ? 'default' : 'pointer' }}
                >
                  <div className="action-header">
                    {!isContextAction && (
                      <input
                        type="checkbox"
                        className="instruction-checkbox"
                        checked={isInstructionCompleted}
                        onChange={() => { }} // Handled by parent div onClick
                        disabled={isReviewMode}
                        aria-label={`Mark instruction as ${isInstructionCompleted ? 'incomplete' : 'complete'}`}
                        onClick={(e) => e.stopPropagation()} // Prevent double toggle
                      />
                    )}
                    <span className="action-text">{uiLanguage === 'zh' && action.text_zh ? action.text_zh : action.text}</span>
                  </div>
                </div>
              );
            }

            // Handle Definition of Done action type
            if (isDefinitionOfDone) {
              return (
                <div
                  key={index}
                  className="action-card definition-of-done-card"
                  onClick={() => !isReviewMode && handleActionClick(action)}
                  style={{ cursor: isReviewMode ? 'default' : 'pointer' }}
                >
                  <div className="action-header">
                    <span className="action-icon">📋</span>
                    <span className="action-text">{uiLanguage === 'zh' && action.text_zh ? action.text_zh : action.text}</span>
                    {action.priority && (
                      <span className={`priority-badge priority-${action.priority}`} aria-label={`Priority: ${action.priority}`}>
                        {action.priority === 'critical' && '🔴 Critical'}
                        {action.priority === 'important' && '🟠 Important'}
                        {action.priority === 'optional' && '⚪ Optional'}
                        {action.priority === 'context' && '🔵 Context'}
                      </span>
                    )}
                  </div>
                  <div className="action-description">
                    <p>Click to view all completion criteria for this step</p>
                  </div>
                </div>
              );
            }

            // Replace legacy "Create Development Task" / "Create MVP" button with a detailed MVP guide
            if (isMvpCreateAction) {
              return (
                <div key={index} className={`action-card mvp-instructions ${isCompleted ? 'completed' : ''}`}>
                  <div className="action-header">
                    <span className="action-icon">{getActionIcon(action.type)}</span>
                    <span className="action-text">How to build your MVP</span>
                    {isCompleted && <span className="completion-badge">✓ Completed</span>}
                  </div>
                  <div className="mvp-steps">
                    <ol>
                      <li>
                        <strong>Step 1:</strong> Define your core action
                        <div className="mvp-step-detail">
                          Write exactly what your tool does in the format: <code>{`My tool will [input] -> [output]`}</code>.
                          Provide 1–2 concrete examples. Example: <em>{`My tool will take a user email & idea summary -> send a 1-question validation survey link and store responses.`}</em>
                          Include clear acceptance criteria (what counts as working).
                        </div>
                      </li>
                      <li>
                        <strong>Step 2:</strong> Choose your building approach
                        <div className="mvp-step-detail">
                          Pick between no-code (Bubble, Softr, Glide) or Code + AI (React/Vercel + simple server). Choose no-code for speed and limited custom logic; choose Code + AI when you need integrations, custom prompts, or data processing.
                        </div>
                      </li>
                      <li>
                        <strong>Step 3:</strong> Build ONLY the core feature
                        <div className="mvp-step-detail">
                          Focus on the single main interaction (the "core action"). Skip accounts, advanced UI polish, multi-tenant plumbing, and non-essential settings. Use placeholder UI and mocked auth if needed.
                        </div>
                      </li>
                      <li>
                        <strong>Step 4:</strong> Deploy online
                        <div className="mvp-step-detail">
                          Deploy a live prototype so testers can click through. Recommended platforms: Vercel, Netlify, or Heroku. Goal: a reachable URL that demonstrates the end-to-end flow.
                        </div>
                      </li>
                      <li>
                        <strong>Step 5:</strong> Create a simple landing page
                        <div className="mvp-step-detail">
                          Include a one-line headline, an animated GIF or short video showing the prototype, and an email signup form (or a link to a Google Form) to capture interest.
                        </div>
                      </li>
                    </ol>
                  </div>
                </div>
              );
            }

            return (
              <div key={index} className={`action-card ${isCompleted ? 'completed' : ''} ${action.priority ? `priority-${action.priority}` : ''}`}>
                <div className="action-header">
                  <span className="action-icon">{getActionIcon(action.type)}</span>
                  <span className="action-text">{uiLanguage === 'zh' && action.text_zh ? action.text_zh : action.text}</span>
                  {action.priority && (
                    <span className={`priority-badge priority-${action.priority}`} aria-label={`Priority: ${action.priority}`}>
                      {action.priority === 'critical' && '🔴 Critical'}
                      {action.priority === 'important' && '🟠 Important'}
                      {action.priority === 'optional' && '⚪ Optional'}
                      {action.priority === 'context' && '🔵 Context'}
                    </span>
                  )}
                  {isCompleted && <span className="completion-badge">✓ Completed</span>}
                </div>
                <button
                  className={`action-button ${isCompleted ? 'completed' : ''} ${isReviewMode ? 'review-mode' : ''}`}
                  onClick={() => !isReviewMode && handleActionClick(action)}
                  disabled={isCompleted || pointsLoading || shouldDisableGenerateAction || isReviewMode || (action.type === 'preRelease' && isCreatingPreRelease)}
                  title={isReviewMode ? 'Actions are disabled in review mode' : undefined}
                >
                  {(() => {
                    // Debug logging for action button text
                    if (action.type === 'ai' && step.stepIndex === 0) {
                      console.log('🔍 Action button debug:', {
                        actionType: action.type,
                        uiLanguage,
                        hasTextZh: !!action.text_zh,
                        text_zh: action.text_zh,
                        text: action.text,
                        isCompleted,
                        shouldDisableGenerateAction,
                        isReviewMode
                      });
                    }

                    if (action.type === 'preRelease' && isCreatingPreRelease) {
                      return (
                        <span className="button-loading-content">
                          <span className="button-loading-spinner button-loading-spinner--inverse" aria-hidden="true"></span>
                          {t('journey.generatingAiProject') || 'Generating AI Project...'}
                        </span>
                      );
                    }
                    if (isReviewMode) return t('journey.reviewMode') || 'Review Mode';
                    if (isCompleted) return t('common.completed') || 'Completed';
                    if (shouldDisableGenerateAction) return t('journey.ideaSelected') || 'Idea Selected';

                    // Use text_zh if available and Chinese is selected, otherwise use text
                    return uiLanguage === 'zh' && action.text_zh ? action.text_zh : action.text;
                  })()}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Manual Definition of Done - Shows AFTER Actions section */}

      {/* Step 0: Idea Generation - Show when idea is selected (Beginner stage) */}
      {step.stepIndex === 0 && selectedIdea && journey?.persona !== 'skilled-maker' && (
        <DefinitionOfDone
          stepName="idea-generation"
          criteria={ideaGenerationDoDCriteria}
          onCriteriaChange={setIdeaGenerationDoDCriteria}
          showProgress={true}
        />
      )}

      {/* Step 0: Rapid Idea Validation - Show for "Have Idea" stage */}
      {step.stepIndex === 0 && rapidIdeaValidationDoDCriteria.length > 0 && journey?.persona !== 'skilled-maker' && (
        <DefinitionOfDone
          stepName="rapid-idea-validation"
          criteria={rapidIdeaValidationDoDCriteria}
          onCriteriaChange={setRapidIdeaValidationDoDCriteria}
          showProgress={true}
        />
      )}

      {/* MVP Definition of Done - show when the step includes an MVP/create action */}
      {hasMvpAction && !isBuildStep && (
        <DefinitionOfDone
          stepName="mvp"
          criteria={mvpDoDCriteria}
          onCriteriaChange={handleMvpCriteriaChange}
          onComplete={() => setMvpAllCompleted(true)}
          showProgress={true}
        />
      )}

      {/* Step 1: Build Your Team - Show for IN_PROGRESS stage */}
      {step.stepIndex === 1 && buildTeamDoDCriteria.length > 0 && (
        <DefinitionOfDone
          stepName="build-team"
          criteria={buildTeamDoDCriteria}
          onCriteriaChange={(updated) => {
            console.log('🔍 Build Team DoD criteria updated:', updated);
            console.log('🔍 All completed?', updated.every(c => c.completed));
            setBuildTeamDoDCriteria(updated);
          }}
          showProgress={true}
        />
      )}

      {/* Step 1: Show DoD for Build Your Solution or Validation with Survey (NOT Build MVP - uses phases) */}
      {step.stepIndex === 1 && validationDoDCriteria.length > 0 && !buildTeamDoDCriteria.length && !isBuildMVPStep && (
        <DefinitionOfDone
          stepName={isBuildStep ? "build" : "validation"}
          criteria={validationDoDCriteria}
          onCriteriaChange={setValidationDoDCriteria}
          showProgress={true}
        />
      )}

      {!isReviewMode && (
        <div className="step-controls">
          {
            (() => {
              // Compute clear flags for whether each type of DoD is satisfied
              // For Skilled Maker persona, skip DoD checks for Step 0 since DoD is not displayed
              const isSkillMakerPersona = journey?.persona === 'skilled-maker';
              const ideaGenRequiredIncomplete = step.stepIndex === 0 && selectedIdea && !isSkillMakerPersona && !ideaGenerationDoDCriteria.every(c => c.completed);
              const rapidIdeaValidationIncomplete = step.stepIndex === 0 && rapidIdeaValidationDoDCriteria.length > 0 && !isSkillMakerPersona && !rapidIdeaValidationDoDCriteria.every(c => c.completed);
              // Check both English and Chinese titles for Build Step
              const isBuildStep = /build your solution/i.test((step && step.title) || '') ||
                /构建您的解决方案/i.test((step && step.title) || '');
              // Check both English and Chinese titles for Build MVP - use titleEn and titleZh from outer scope
              const isBuildMVP = /build.*mvp/i.test(titleEn) || /构建.*mvp/i.test(titleZh);
              // Check both English and Chinese titles for Build Team step
              const isBuildTeam = /build your team/i.test((step && step.title) || '') ||
                /组建您的团队/i.test((step && step.title) || '');
              const buildTeamRequiredIncomplete = step.stepIndex === 1 && isBuildTeam && buildTeamDoDCriteria.length > 0 && !buildTeamDoDCriteria.every(c => c.completed);

              // For Build MVP step, check if all instructions are completed OR all DoD criteria are met
              const instructionActions = step.actions.filter(action => action.type === 'instruction' && action.priority !== 'context');
              const allInstructionsCompleted = instructionActions.length > 0 &&
                instructionActions.every(action => {
                  const actionHash = action.text.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
                  return completedInstructions.has(`${step.id}-${actionHash}`);
                });

              // For phase-based steps (like Build Product MVP), check if all actions from all phases are completed
              const isPhaseBasedStep = hasPhases && actionsByPhase;
              const allPhaseActionsCompleted = isPhaseBasedStep ?
                Object.values(actionsByPhase).flat()
                  .filter(action => action.type === 'instruction' && action.priority !== 'context')
                  .every(action => {
                    const actionHash = action.text.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
                    return completedInstructions.has(`${step.id}-${actionHash}`);
                  }) : false;

              // For Build Your Solution we rely only on the validationDoDCriteria (single item) and do not require a validationSurvey
              // Exclude Build Team step from this validation logic
              const validationRequiredIncomplete = step.stepIndex === 1 && !isBuildTeam && (
                isBuildStep ? !validationDoDCriteria.every(c => c.completed) :
                  isBuildMVP ? (!allInstructionsCompleted && !allPhaseActionsCompleted && !validationDoDCriteria.every(c => c.completed)) :
                    (!validationSurvey || !validationDoDCriteria.every(c => c.completed))
              );

              // Debug logging for Build Team step (moved after all variables are declared)
              // Will log after validationRequiredIncomplete is declared
              const mvpRequiredIncomplete = hasMvpAction && !mvpDoDCriteria.every(c => c.completed);

              // Debug logging for Build Team step - NOW all variables are declared
              if (step.stepIndex === 1 && isBuildTeam) {
                console.log('🔍 Build Team Button Logic:', {
                  stepIndex: step.stepIndex,
                  isBuildTeam,
                  criteriaLength: buildTeamDoDCriteria.length,
                  criteria: buildTeamDoDCriteria,
                  allCompleted: buildTeamDoDCriteria.every(c => c.completed),
                  buildTeamRequiredIncomplete,
                  ideaGenRequiredIncomplete,
                  rapidIdeaValidationIncomplete,
                  validationRequiredIncomplete,
                  mvpRequiredIncomplete,
                  finalDisabled: ideaGenRequiredIncomplete || rapidIdeaValidationIncomplete || validationRequiredIncomplete || buildTeamRequiredIncomplete
                });
              }

              // Check if this is Step 0 (Generate and Validate Ideas) and if AI idea is required
              const isGenerateIdeasStep = step.stepIndex === 0 && (
                /generate.*ideas/i.test((step && step.title) || '') ||
                /validate.*ideas/i.test((step && step.title) || '') ||
                /生成.*创意/i.test((step && step.title) || '') ||
                /验证.*创意/i.test((step && step.title) || '')
              );
              const aiIdeaRequired = isGenerateIdeasStep && !selectedIdea;
              
              // Debug logging for Generate Ideas step
              if (step.stepIndex === 0) {
                console.log('🔍 Generate Ideas Button Logic:', {
                  stepIndex: step.stepIndex,
                  stepTitle: step.title,
                  isGenerateIdeasStep,
                  hasSelectedIdea: !!selectedIdea,
                  aiIdeaRequired,
                  selectedIdea
                });
              }
              
              // Check if this is Step 2 (Test with Users) and if pre-release project is required
              const isTestWithUsersStep = step.stepIndex === 2 && (
                /test with users/i.test((step && step.title) || '') ||
                /与用户测试/i.test((step && step.title) || '') ||
                /测试用户/i.test((step && step.title) || '')
              );
              const preReleaseProjectRequired = isTestWithUsersStep && !preReleaseProject;
              
              // Debug logging for Test with Users step
              if (step.stepIndex === 2) {
                console.log('🔍 Test with Users Button Logic:', {
                  stepIndex: step.stepIndex,
                  stepTitle: step.title,
                  isTestWithUsersStep,
                  hasPreReleaseProject: !!preReleaseProject,
                  preReleaseProjectRequired,
                  preReleaseProject
                });
              }

              // If this step contains an MVP action, require only the MVP Definition of Done
              let disabled: boolean;
              let title: string;

              // Debug logging for Build MVP step in Chinese mode
              if (isBuildMVP) {
                console.log('🔍 Build MVP Button Logic:', {
                  stepIndex: step.stepIndex,
                  stepTitle: step.title,
                  stepTitle_zh: step.title_zh,
                  titleEn,
                  titleZh,
                  isBuildMVP,
                  hasPhases,
                  isPhaseBasedStep,
                  allInstructionsCompleted,
                  allPhaseActionsCompleted,
                  validationDoDCriteriaCompleted: validationDoDCriteria.every(c => c.completed),
                  validationDoDCriteria,
                  instructionActions: instructionActions.length,
                  completedInstructions: completedInstructions.size,
                  actionsByPhase: actionsByPhase ? Object.keys(actionsByPhase) : null
                });
              }

              if (aiIdeaRequired) {
                // For Generate Ideas step, require AI idea to be selected
                disabled = true;
                title = t('journey.selectAIIdeaFirst') || 'Select an AI-generated idea first to enable this button';
              } else if (preReleaseProjectRequired) {
                // For Test with Users step, require pre-release project to be created
                disabled = true;
                title = t('journey.createPreReleaseProjectFirst') || 'Create an AI-Enhanced Pre-Release Project first to enable this button';
              } else if (hasMvpAction) {
                // rely on the mvpAllCompleted flag which updates whenever criteria change
                disabled = !mvpAllCompleted;
                title = disabled
                  ? 'Complete the MVP Definition of Done to enable this button'
                  : 'Complete this step and move to the next one';
              } else if (hasPhases && isPhaseBasedStep) {
                // For ALL phase-based steps (Build MVP, Get Early Users, Scale and Grow, etc.)
                // Enable only when all phase actions are completed
                // For Build MVP specifically, also check DoD criteria OR all phase actions
                const hasDoDCriteria = validationDoDCriteria.length > 0;
                
                if (isBuildMVP && hasDoDCriteria) {
                  // Build MVP: Enable if all phase actions OR all DoD criteria are completed
                  disabled = !allPhaseActionsCompleted && !validationDoDCriteria.every(c => c.completed);
                  title = disabled
                    ? 'Complete all actions from all phases OR all Definition of Done criteria to enable this button'
                    : 'Complete this step and move to the next one';
                } else {
                  // Other phase-based steps: Require ALL phase actions to be completed
                  disabled = !allPhaseActionsCompleted;
                  title = disabled
                    ? 'Complete all actions from all phases to enable this button'
                    : 'Complete this step and move to the next one';
                }
              } else {
                // Disabled if any applicable required group is incomplete
                disabled = ideaGenRequiredIncomplete || rapidIdeaValidationIncomplete || validationRequiredIncomplete || buildTeamRequiredIncomplete;
                title = disabled
                  ? 'Complete all required Definition of Done criteria to enable this button'
                  : 'Complete this step and move to the next one';
              }

              return (
                <button
                  className="complete-button primary"
                  onClick={handleCompleteStep}
                  disabled={disabled}
                  title={title}
                >
                  {t('journey.completeStep')}
                </button>
              );
            })()
          }
        </div>
      )}

      {showEarningSuggestions && (
        <div className="earning-suggestions-modal">
          <div className="modal-overlay" onClick={() => setShowEarningSuggestions(null)} />
          <div className="modal-content">
            <div className="modal-header">
              <h3>{t('journey.needMorePoints')}</h3>
              <button
                className="close-button"
                onClick={() => setShowEarningSuggestions(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {(() => {
                const action = step.actions.find(a => a.type === showEarningSuggestions);
                const affordability = actionAffordability.get(showEarningSuggestions!);
                if (!action || !affordability) return null;

                const suggestions = getEarningSuggestions(affordability.shortfall);

                return (
                  <>
                    <p>
                      {t('journey.youHave')} <strong>{affordability.currentPoints} {t('journey.points')}</strong>. {affordability.shortfall} {t('journey.pointsRequired')}.
                    </p>

                    <div className="earning-suggestions">
                      <h4>{t('journey.quickWaysToEarn')}</h4>
                      <ul>
                        {suggestions.suggestions.map((suggestion, index) => (
                          <li key={index} className="suggestion-item">
                            <div className="suggestion-header">
                              <span className="activity">{suggestion.activity}</span>
                              <span className="points">+{suggestion.points} pts</span>
                            </div>
                            <div className="suggestion-details">
                              <span className="description">{suggestion.description}</span>
                              <span className="time">{suggestion.timeEstimate}</span>
                            </div>
                          </li>
                        ))}
                      </ul>

                      <div className="quickest-path">
                        <strong>Quickest path:</strong> {suggestions.quickestPath}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button
                className="close-modal-button"
                onClick={() => setShowEarningSuggestions(null)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Definition of Done Modal */}
      <DefinitionOfDoneModal
        step={step}
        isOpen={isDoDModalOpen}
        onClose={() => setIsDoDModalOpen(false)}
      />
      </div>
    </>
  );
};
